import React from 'react';
import { Box, TableRow } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { HeadCell, HoverLink, Table, TableCell, Typography } from '../../../../ui-kit';
import { translate } from '../../../../utils';
import { routePaths } from '../../../../routePaths';
import { usePriceService } from '../../../../utils/hooks/priceService';
import { useUser } from '../../../../utils/hooks/auth';
import { DeliveryListHeadCell, DeliveryListTableProps } from './types';

const headCells: HeadCell<DeliveryListHeadCell>[] = [
    { id: 'stockCard', label: translate('Stock card') },
    { id: 'priceWithoutVat', label: translate('Price without VAT'), align: 'right' },
    { id: 'quantity', label: translate('Quantity'), numeric: true, align: 'right' },
    { id: 'totalCost', label: translate('Total cost without VAT'), numeric: true, align: 'right' },
];

const DeliveryListTable = ({ deliveryListItems }: DeliveryListTableProps) => {
    const { user: { defaultCurrency: { currencyCode } } } = useUser();
    const { priceService } = usePriceService();
    const history = useHistory();
    const referrer = history.location.pathname;

    return (
        <Table isFirstColumnSticky headCells={headCells}>
            {deliveryListItems.map(row => (
                <TableRow tabIndex={-1} key={row.id}>
                    {headCells.map((cell, index) => {
                            let cellValue: React.ReactNode;

                            const {
                                quantity,
                                priceWithoutVat,
                                stockCard: { id: stockCardId, name, conversionUnit, convertedAmount, storageUnit },
                            } = row;

                            if (cell.id === 'stockCard') {
                                const stockCardUrl = `${routePaths.warehouseCards}/${stockCardId}`;

                                let postfix = storageUnit.toString();
                                if (conversionUnit && convertedAmount) {
                                    postfix = `${convertedAmount} ${conversionUnit}`;
                                }

                                cellValue = (
                                    <HoverLink wide to={{ pathname: stockCardUrl, state: { referrer } }}>
                                        <Box display='flex'>
                                            <Typography fontWeight='medium'>{name}</Typography>
                                            {postfix
                                                ? (
                                                    <Box ml={0.5}>
                                                        <Typography fontWeight='medium' color='textSecondary'>{postfix}</Typography>
                                                    </Box>
                                                )
                                                : null}
                                        </Box>
                                    </HoverLink>
                                );
                            } else if (cell.id === 'priceWithoutVat') {
                                cellValue = (
                                    <Typography>
                                        {priceService.toLocaleString(priceWithoutVat, currencyCode)}
                                    </Typography>
                                );
                            } else if (cell.id === 'totalCost') {
                                const total = priceService.multiply(priceWithoutVat, quantity, currencyCode);
                                cellValue = (
                                    <Typography>
                                        {priceService.toLocaleString(total, currencyCode)}
                                    </Typography>
                                );
                            } else if (cell.id === 'quantity') {
                                cellValue = (
                                    <Typography>
                                        {`${quantity} ${storageUnit}`}
                                    </Typography>
                                );
                            }
                            return (
                                <TableCell align={cell.align} isSticky={index === 0} key={`${cell.id} - ${row.id}`}>
                                    {cellValue}
                                </TableCell>
                            );
                        })}
                </TableRow>
                ))}
        </Table>
    );
};

export default DeliveryListTable;
